import React from "react";
import clsx from "clsx";
import StatusPill from "../StatusPill";
import { roomColor } from "../FilterBar";
import InlineText from "./cells/InlineText";
import DurationEditor from "./cells/DurationEditor";
import { ROW_STATE_STYLES } from "./rowUtils";
import { makeRoomMoveDragStart } from "./roomDnd";

/**
 * Compact, indented child row rendered below an expanded parent block.
 * Subtasks share the parent's room/time, so this row shows only title, status,
 * duration, and a left-bracket glyph in place of a start time.
 */
export default function SubtaskRow({
  subtask,
  room,
  rowH,
  leftOffsets,
  crew,
  onPatchTask,
  onOpenHistory,
  onSelectTask,
}) {
  const stateStyle = ROW_STATE_STYLES[subtask.status] || "";

  const handleContextMenu = (e) => {
    e.preventDefault();
    onOpenHistory && onOpenHistory();
  };

  const cellBase = "border-r border-line border-b border-line-subtle flex items-center px-2 text-xs relative bg-bg-row";
  const stickyCell = (extra = "") => clsx(cellBase, stateStyle, extra);

  const onTitleCommit = (newTitle) => {
    if (newTitle !== subtask.title) onPatchTask(subtask.id, { title: newTitle });
  };

  return (
    <>
      {/* START — left-bracket glyph showing subtask membership */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[0], zIndex: 10 }}
        onContextMenu={handleContextMenu}
      >
        <span className="mono text-2xs text-ink-mute pl-2">└</span>
      </div>
      {/* FINISH — empty */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[1], zIndex: 10 }}
        onContextMenu={handleContextMenu}
      />
      {/* DUR — typable, only meaningful for subtasks */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[2], zIndex: 10 }}
        onContextMenu={handleContextMenu}
      >
        <DurationEditor
          value={subtask.duration_min}
          onCommit={(mins) => onPatchTask(subtask.id, { duration_min: mins })}
          dataTestid={`subtask-dur-${subtask.id}`}
        />
      </div>
      {/* ROOM — color stripe only, no name */}
      <div
        className={stickyCell("pl-0")}
        style={{ height: rowH, position: "sticky", left: leftOffsets[3], zIndex: 10 }}
        onContextMenu={handleContextMenu}
      >
        <span
          className="self-stretch w-1 mr-2 opacity-50"
          style={{ background: roomColor(room?.color_token) }}
        />
        <span className="mono text-2xs text-ink-mute uppercase tracking-widest">subtask</span>
      </div>
      {/* TASK — indented title; also the drag handle. Dragging onto another
          task = re-parent to that task's group; dragging onto whitespace =
          unparent. */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[4], zIndex: 10 }}
        onContextMenu={handleContextMenu}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onSelectTask && onSelectTask();
        }}
        data-testid={`subtask-${subtask.id}`}
        draggable
        onDragStart={makeRoomMoveDragStart({
          rowKey: `ft:${subtask.id}`,
          selection: [],
          isSubtask: true,
        })}
        title="Drag onto another task to re-group · drag to whitespace to ungroup"
      >
        <div className="flex items-center gap-2 w-full min-w-0 pl-6">
          {subtask.priority === "critical" && (
            <span className="text-red mono text-2xs uppercase tracking-widest">‼</span>
          )}
          <InlineText
            value={subtask.title}
            onCommit={onTitleCommit}
            dataTestid={`subtask-title-${subtask.id}`}
          />
        </div>
      </div>
      {/* STATUS */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[5], zIndex: 10 }}
        onContextMenu={handleContextMenu}
      >
        <StatusPill
          status={subtask.status}
          onChange={(next) => onPatchTask(subtask.id, { status: next })}
          dataTestid={`subtask-status-${subtask.id}`}
        />
      </div>
      {/* NOTES */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[6], zIndex: 10 }}
        onContextMenu={handleContextMenu}
      >
        {subtask.notes && (
          <span className="text-ink-mute truncate text-2xs" title={subtask.notes}>
            {subtask.notes}
          </span>
        )}
      </div>
      {/* CREW columns — empty for subtasks (they inherit parent crew visually) */}
      {crew.map((c) => (
        <div
          key={c.id}
          className={clsx(cellBase, "bg-bg-row")}
          style={{ height: rowH }}
          onContextMenu={handleContextMenu}
        />
      ))}
    </>
  );
}
